import { useRef, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messagesApi, tradeApi } from '@/lib/api/endpoints';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSession } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
import { emailValidation } from '@/lib/utils/validation';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2 } from 'lucide-react';
export interface EnquiryTypeOption {
  value: string;
  label: string;
  enabled?: boolean;
}
interface EnquiryFormProps {
  resourceType: string;
  resourceId: number;
  enquiryTypes: EnquiryTypeOption[];
  onSuccess?: () => void;
  className?: string;
  context?: string;
  showSubject?: boolean;
  showType?: boolean;
  showPhone?: boolean;
  showTitle?: boolean;
  sessionRequiredToSubmit?: boolean;
  modalSignup?: boolean;
  apiRoute?: string;
  defaultEnquiryType?: string;
  /** Optional message to display above the form */
  introMessage?: string;
  /** Override the default title. Pass a ReactNode for full control. */
  title?: React.ReactNode;
  /** Custom content to render inside the enquiry form block (e.g. contact details) */
  customContent?: React.ReactNode;
  /** Override the default "Send" submit button label */
  submitLabel?: string;
}
interface EnquiryFormData {
  senderName: string;
  senderEmail: string;
  senderPhone: string;
  enquiryType: string;
  subject: string;
  message: string;
}
export default function EnquiryForm({
  resourceType,
  resourceId,
  enquiryTypes,
  onSuccess,
  className = '',
  context,
  showSubject = true,
  showType = false,
  showPhone = true,
  showTitle = true,
  sessionRequiredToSubmit = false,
  modalSignup = false,
  apiRoute,
  defaultEnquiryType,
  introMessage,
  title,
  customContent,
  submitLabel
}: EnquiryFormProps) {
  const enabledTypes = useMemo(() => enquiryTypes.filter(type => type.enabled !== false), [enquiryTypes]);
  const [showSuccess, setShowSuccess] = useState(false);
  const queryClient = useQueryClient();
  const {
    data: session
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const pendingPayloadRef = useRef<any>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: {
      errors
    }
  } = useForm<EnquiryFormData>({
    defaultValues: {
      senderName: session?.user?.name || '',
      senderEmail: session?.user?.email || '',
      senderPhone: '',
      enquiryType: defaultEnquiryType || enabledTypes[0]?.value || ''
    }
  });
  const enquiryType = watch('enquiryType');
  const sendEnquiryMutation = useMutation({
    mutationFn: (data: any) => messagesApi.sendEnquiry(data, {
      route: apiRoute
    }),
    onSuccess: () => {
      toast.success('Enquiry sent successfully');
      reset();
      setShowSuccess(true);
      queryClient.invalidateQueries({
        queryKey: ['messaging']
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorData = error.response?.data?.error;
      toast.error(errorData?.message || 'Failed to send enquiry');
    }
  });
  const buildPayload = (data_0: EnquiryFormData) => {
    const payload: any = {
      resourceType,
      resourceId,
      enquiryType: data_0.enquiryType,
      message: data_0.message,
      senderName: data_0.senderName,
      senderEmail: data_0.senderEmail,
      senderPhone: data_0.senderPhone || undefined,
      data: {}
    };

    // Only include subject if it's shown and has a value
    if (showSubject && data_0.subject) {
      payload.subject = data_0.subject;
    }

    // Add context metadata if provided
    if (context) {
      payload.context = context;
    }
    return payload;
  };
  const onSubmit = (data_1: EnquiryFormData) => {
    setShowSuccess(false);
    const payload_0 = buildPayload(data_1);

    // If session is required but user isn't signed in, show sign-in modal
    if (sessionRequiredToSubmit && !session) {
      pendingPayloadRef.current = payload_0;
      showSignInModal({
        initialEmail: data_1.senderEmail,
        description: <div className="font-bold p-6">
            <div className="text-lg">
              Please complete this 20 second signup to send your enquiry and get a VIP response
            </div>
          </div>,
        preferFullscreenForNewUsers: !modalSignup,
        onSuccess: () => {
          // onSuccess only fires for returning users (not new users going through onboarding)
          const pending = pendingPayloadRef.current;
          if (pending) {
            pendingPayloadRef.current = null;
            sendEnquiryMutation.mutate(pending);
          }
        }
      });
      return;
    }
    sendEnquiryMutation.mutate(payload_0);
  };
  return <div className={`bg-white rounded-lg shadow ${className}`}>
      {showTitle && (title !== undefined ? <>{title}</> : <h2 className="text-xl font-semibold mb-4">Send Enquiry</h2>)}

      {introMessage && <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{introMessage}</p>}

      {customContent && <div className="mb-8">{customContent}</div>}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Sender Name */}
        <div>
          <Label htmlFor="senderName">Your Name</Label>
          <Input id="senderName" {...register('senderName', {
          required: 'Name is required'
        })} placeholder="Enter your name" />
          {errors.senderName && <p className="text-sm text-red-600 mt-1">{errors.senderName.message}</p>}
        </div>

        {/* Sender Email */}
        <div>
          <Label htmlFor="senderEmail">Your Email</Label>
          <Input id="senderEmail" type="email" {...register('senderEmail', emailValidation)} placeholder="Enter your email" />
          {errors.senderEmail && <p className="text-sm text-red-600 mt-1">{errors.senderEmail.message}</p>}
        </div>

        {/* Sender Phone */}
        {showPhone && <div>
            <Label htmlFor="senderPhone">Your Phone (optional)</Label>
            <Input id="senderPhone" type="tel" {...register('senderPhone')} placeholder="+1 (555) 123-4567" />
          </div>}

        {/* Enquiry Type */}
        {showType && <div>
            <Label htmlFor="enquiryType">Enquiry Type</Label>
            <Select value={enquiryType} onValueChange={val => setValue('enquiryType', val)}>
              <SelectTrigger>
                <SelectValue placeholder="Select enquiry type" />
              </SelectTrigger>
              <SelectContent>
                {enabledTypes.map(type_0 => <SelectItem key={type_0.value} value={type_0.value}>
                    {type_0.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>}

        {/* Subject */}
        {showSubject && <div>
            <Label htmlFor="subject">Subject</Label>
            <Input id="subject" {...register('subject', {
          required: 'Subject is required'
        })} placeholder="Enter subject" />
            {errors.subject && <p className="text-sm text-red-600 mt-1">{errors.subject.message}</p>}
          </div>}

        {/* Message */}
        <div>
          <Label htmlFor="message">Message</Label>
          <Textarea id="message" {...register('message', {
          required: 'Message is required'
        })} placeholder="Enter your message" rows={4} className="min-h-[6lh]" />
          {errors.message && <p className="text-sm text-red-600 mt-1">{errors.message.message}</p>}
        </div>

        {showSuccess && <Alert className="border-green-200 bg-green-50 text-green-800">
            <CheckCircle2 className="text-green-600" />
            <AlertTitle>Enquiry sent</AlertTitle>
            <AlertDescription>
              Your enquiry has been sent successfully. We'll get back to you soon.
            </AlertDescription>
          </Alert>}

        <Button type="submit" disabled={sendEnquiryMutation.isPending}>
          {sendEnquiryMutation.isPending ? 'Sending...' : submitLabel || 'Send'}
        </Button>
      </form>
    </div>;
}